import _ from 'lodash';
import { useContext, useEffect, useRef, useState } from 'react';
import { faSync } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ApplicableTableSettings, codesObltypeList } from '../../../../tableSettings';
import useFastStorage from '../../../../hooks/useFastStorage';
import useFunction from '../../../../hooks/useFunction';
import useGlobalOkDialog from '../../../../hooks/useGlobalOkDialog';
import ApplicationContext from '../../../../ApplicationContext';
import { Portal } from 'react-portal';
import { JobNames } from '../../../../framework.core/JobNames';
import { CodeBLTypeListItem } from '../../../../fms.data/models/CodeBLTypeListItem';
import { Search, getInitialSearch } from '../../../../filtering';
import { ApiResult, InsertResult } from '../../../../framework.core/data/ApiResult';
import { SQLType } from '../../../../framework.core/data/SQLInfo';
import TextBox from '../../../../ui/TextBox';
import EditableTable, { CellData, TableCellTypes } from '../../../../ui/tables/editing/EditableTable';
import { FmsSecTask } from '../../../../fms.core/constants';
import GreenButton from '../../../../ui/GreenButton';
import { checkTabIsDirty } from '../../ReferenceFiles';
import { duplicateCell, findSecurityTask } from '../../../../util';
import BlackInverseButton from '../../../../ui/BlackInverseButton';
import constants from '../../../../constants';
import IFmsReferenceFilesContext from '../../IfmsReferenceFilesContext';
import CloseButton from '../../../../ui/CloseButton';
import useFunctionKeys from '../../../../hooks/useFunctionKeys';
import { RefreshIconButton2 } from '../../../../ui/IconButton';

function TypeRenderer({data, row, cell}:any) {
  const changeLineItem = data[0].changeLineItem;
  const l = data[row.index];
  return (
    <TextBox textId={`mblType-${row.index}`} value={l.mblType} maxLength={30} onChange={e => 
      changeLineItem(row.index, {mblType: e.target.value, isUpdate:true})} />
  );
}

function formatElement(data:any) {
  return data;
}

function formatData(data:Array<any>) {
  return data.map(d => formatElement(d));
}

function OriginalBlTypes() {
  useFunction({invokers: {
    ifmsReferenceFilesSave: save,
    ifmsReferenceFilesCancel: cancelOperation,
    //ifmsReferenceFilesCancel: () => {},
  }});
  useFunctionKeys({onSave: handleSaveAndClose});
  
  const appContext = useContext(ApplicationContext);
  const refContext = useContext(IFmsReferenceFilesContext);
  const [showGlobalOkDialog] = useGlobalOkDialog();

  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useFastStorage(`ifms-referenceFiles-haschanges`, false);
  const [data, _setData, removeData] = useFastStorage(`ifms-referenceFiles-obltype-list`, []);
  
  const savedDataRef = useRef<Array<CodeBLTypeListItem>>([]);
  const oriData = useRef<CodeBLTypeListItem[]>([]);
  //const addingToTheFirstLine = useRef(false);

  const listItemType = 'CodeBLTypeListItem';

  const tableHeight = 520;
  const listSec = findSecurityTask(appContext, FmsSecTask.CodeBLType);
  const [focusFirstCell, setFocusFirstCell] = useState({ dataLength: 0, focus: false });

  function handleSaveAndClose() {
    const saveBtn = document.getElementById('detail-save-button');
    if (saveBtn) {
      saveBtn.focus();
      const modals = document.querySelector('[class^="ReactModal__Content"]');
      if (!modals)
        saveBtn.click();
    }
  }

  function setData(d:Array<any>, callByFetchData: boolean = false) {
    setFocusFirstCell({ dataLength: d.length, focus: callByFetchData });
    _setData(d, null, { forceClone: callByFetchData });
    setHasChanges(checkTabIsDirty(CodeBLTypeListItem, oriData.current, d));
  }

  async function fetchData(api:any, search:Search) {
    setLoading(true);
    const searchModel = CodeBLTypeListItem.calculateSearchParameters(search);

    const apiResult = await api.getListItems(JobNames.BLType,
      listItemType,
      searchModel,
      1,
      search.pageSize);
    const data = apiResult && apiResult.entities ? apiResult.entities : [];

    oriData.current = formatData(data);
    setData(formatData(data), true);

    savedDataRef.current = data;

    setLoading(false);
    //setHasChanges(false);
  }

  useEffect(() => {
    if (data.length === 0)
      reload();
    else {
      oriData.current = _.cloneDeep(data);
      savedDataRef.current = _.cloneDeep(data);
    }
    return () => {
      removeData();
    };
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  function reload() {
    fetchData(appContext.api, getInitialSearch());
  }

  async function cancelOperation(){
    setHasChanges(false);
    removeData();
    return true;
  }

  function deleteRow(index: number): boolean {
    if (!data) {
      return false;
    }

    const element = data[index];

    if (element.isNew === undefined) {
      let deleteRow = savedDataRef.current.find(x => x.mblType === element.mblType);
      if (deleteRow) {
        deleteRow.isDelete = true;
        //setHasChanges(true);

      }
    }
    data.splice(index, 1);
    setData(data);
    return true;
  }

  function applyToLineItem(rowIndex: number, change: Partial<CodeBLTypeListItem>|null) {
    if (!change || !data) {
      return;
    }
    const newData = [...data];
    const checkData = newData[rowIndex];
    if (!change.isNew && change.mblType !== checkData.mblType) {
      _.merge(newData[rowIndex], { ...change, hasBeenChanged: true });
      //setHasChanges(true);
      setData(newData);
    }
  }

  function addLineItem() {
    return {
      setDataFunc: (list: any) => setData(list),
      curListData: data,
      newData: { mblType: '', isNew: true }
    };
  }
 


  async function save() {
    if (!data) {
      return;
    }

    let validData = data.filter((x:any) => x.mblType);
    
    let errMsg = '';
    const dataToSave = validData.filter((x: any) => x.isNew || x.hasBeenChanged === true); // new data is always last so index check works
    const dataToDelete = savedDataRef.current.filter((x) => x.isDelete);
    if(dataToSave.length === 0 && dataToDelete.length === 0) {
      if (hasChanges) reload();
      return;
    }
    setLoading(true);

    for (let i = 0; i < dataToDelete.length; ++i) {
      const s = dataToDelete[i];
      const apiResult:ApiResult<InsertResult> = await appContext.api.saveListItem(listItemType, 
      SQLType.Delete, 
      _.omit(s, ['isDelete']));

      if(!apiResult || !apiResult.success)
        errMsg = apiResult?.message ?? constants.SAVE_ERROR_MESSAGE;
    }

    if (dataToSave.length > 0) {
      const cidData = dataToSave.map((a: CodeBLTypeListItem) => a.cid?.toLowerCase()) as Array<String>;
      
      const secondSave = dataToSave.filter((x: CodeBLTypeListItem) => cidData.includes(x.mblType.toLowerCase()));
      const firstSave = dataToSave.filter((x: CodeBLTypeListItem) => !secondSave.includes(x));

      const isDuplicate = await duplicateCell(dataToSave, data, appContext, listItemType, showGlobalOkDialog, JobNames.BLType, 'mblType');
      if (isDuplicate) {
        setLoading(false);
        return;
      }
      if (firstSave.length > 0) {
        const apiResult: ApiResult<InsertResult> = await appContext.api.saveList(listItemType, firstSave);
        if (!apiResult || !apiResult.success)
          errMsg += (errMsg ? '\n' : '') + (apiResult?.message ?? constants.SAVE_ERROR_MESSAGE);
        else {
          firstSave.forEach((x: any) => {
            const index = data.findIndex((d: any) => d.mblType === x.mblType);
            if (index >= 0) {
              data[index].cid = x.mblType;
              data[index].hasBeenChanged = false;
            }
            if (x.isNew) {
              delete data[index].isNew;
            }
          })
        }
      }
      if (secondSave.length > 0) {
        const apiResult2: ApiResult<InsertResult> = await appContext.api.saveList(listItemType, secondSave);
        if (!apiResult2 || !apiResult2.success)
          errMsg += (errMsg ? '\n' : '') + (apiResult2?.message ?? constants.SAVE_ERROR_MESSAGE);
        else {
          secondSave.forEach((x: any) => {
            const index = data.findIndex((d: any) => d.mblType === x.mblType);
            if (index >= 0) {
              data[index].cid = x.mblType;
              data[index].hasBeenChanged = false;
            }
            if (x.isNew) {
              delete data[index].isNew;
            }
          })
        }
      }
    }

    if(errMsg) {
      await showGlobalOkDialog(<p>{errMsg}</p>);
      setLoading(false);
      return;
    }

    // setData(data);
    // savedDataRef.current = _.cloneDeep(data);
    // oriData.current = _.cloneDeep(data);

    // setLoading(false);
    // setHasChanges(false);
    reload();

    await showGlobalOkDialog(<p>The data has been saved</p>);
  }

  const tableOptions:ApplicableTableSettings = {
    customTableId: 'codes-obltype-list',
    tableName: 'oblTypeList',
    ...codesObltypeList,
    height: `${tableHeight}px`,
    data: data.map((x: any) => ({...x, changeLineItem: applyToLineItem})),
    rowRenderers: [
      { accessor: 'indicator' },
      { accessor: 'mblType', EditCell: TypeRenderer },
    ],
    getCellData: (accessor: any, rowData: any, rowIndex: number): CellData | undefined => {
      if (accessor === 'mblType')
        return { type: TableCellTypes.Text, value: data[rowIndex].mblType };
      else
        return undefined;
    },
    isCellLock: (accessor: string, rowData: any): boolean => {
      if(!(listSec?.grantAdd || listSec?.grantEdit || listSec?.grantDel))
        return true
      else
        return false;
    },
    useEditControls: (listSec?.grantAdd || listSec?.grantEdit || listSec?.grantDel),
    //useTableHelperText: (listSec?.grantAdd || listSec?.grantEdit || listSec?.grantDel),
  };

  //const typeCount = data ? data.length : 0;

  return (
    <div style={{marginTop: '40px' }}>
      {/* <Portal node={document && document.getElementById('settings-placeholder')}>
        <h1>Ocean <FontAwesomeIcon icon={faAngleRight} />&nbsp;Original B/L Type</h1>
      </Portal> */}

      {/* <div className="oi-doc-title-sub-row">

           <div style={{marginLeft: '10px', fontSize: '11px', fontWeight: 'bold'}}>
            Double Click B/L Type to Edit
          </div> 
        </div> */}

      <Portal node={document && document.getElementById('settings-command-placeholder')}>
        <div style={{ display: 'flex', width: '100px' }}>
          <div className="ifms-common-bar-element-center">
            <GreenButton size="regular" disabled={loading} onClick={save}><span style={{ textDecoration: 'underline' }}>S</span>ave</GreenButton>
          </div>
          <div style={{ margin: 'auto' }}></div>
          <div className="ifms-common-bar-element-center">
            <CloseButton onlyIcon name='settings-close' mode='light' onClick={refContext.cancel} />
          </div>
        </div>
      </Portal>
      <div style={{ display: 'flex', width: '432px' }}>
        <div style={{ marginLeft: 'auto' }}></div>
        <div style={{ position: 'relative', top: '-4px' }}>
          {//<SecondaryButton name='codes-originalbl-type-add-button' size="regular" disabled={!listSec?.grantAdd} onClick={() => { addingLineToFirstLine() }}><FontAwesomeIcon icon={faPlus} />&nbsp; Add Line</SecondaryButton> &nbsp; 
          }
          {/* <BlackInverseButton name='list-screen-refresh' size="large" onClick={() => { reload() }} data-tooltip="Refresh" >
            <FontAwesomeIcon color='#47C83E' icon={faSync} />
          </BlackInverseButton> */}
          <RefreshIconButton2 name='list-screen-refresh' onClick={() => { reload() }} data-tooltip="Refresh"/>
        </div>
      </div>
      <div style={{ width: '432px' }}>
        <EditableTable
          initializeFocusedCell={focusFirstCell}
          tableOptions={tableOptions}
          deleteLine={deleteRow}
          addLine={addLineItem}
          canDeleteRow={rowNumber => listSec?.grantDel ?? false}
        />
      </div>
    </div>
  );
}

export default OriginalBlTypes;
